import { KnownProduct } from "../Product/KnownProduct.tsx";

import * as yup from "yup";
import {
  Control,
  Controller,
  FieldValues,
  useForm,
  UseFormRegister,
  UseFormWatch,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CheckBox,
  MultipleSelect,
  MultipleSelectOption,
  PrimaryButton,
  Select,
  SelectOption,
  TextInput,
} from "@normasteaching/norma-ui";
import { PropsWithChildren, useEffect, useState } from "react";
import { NormasTeachingProductDTO } from "../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import {
  buildPaymentMethodsValidationSchema,
  getAvailablePaymentMethods,
  getPaymentMethodConfig,
} from "../../NormasTeachingCheckout/PaymentMethodRegistryUtils.ts";
import {
  getPaymentMethodName,
  PaymentMethod,
} from "../../NormasTeachingCheckout/PaymentMethod.ts";
import { PaymentMethodConfigsDTO } from "../../NormasTeachingCheckout/PaymentMethodConfiguration.ts";
import { OneOnOneLessonType } from "./OneOnOneLessonType.tsx";

export type CreateNormasTeachingProductFormValues = {
  productName: string;
  variantProductName: string;
  priceInEuros: number;
  productClass: KnownProduct;
  purchaseAcTags?: string[];
  refundAcTags?: string[];
  bundledProductIds?: string[];
  peopleCount: number;
  productDescription?: string;

  fpsLessonsDurationInWeeks?: number;
  fpsUnlimitedLessonsPerWeek?: boolean;
  fpsLessonsPerWeek?: number;
  fpsTotalLessons?: number;
  fpsExtraWeek?: boolean;
  fpsWarrantyEnabled?: boolean;
  fpsWarrantyMinAttendedLessons?: number;
  userGroupSlug?: string;
  doNotEnroll: boolean;
  doNotMerge: boolean;

  selectedPaymentMethods: PaymentMethod[];

  analyticsTag?: string;

  isCouponEnabled?: boolean;
  oneOnOne?: boolean;
  oneOnOneTotalLessons?: number;
  oneOnOneDurationInWeeks?: number;
  oneOnOneMaxCancellations?: number;
  oneOnOneLessonType?: OneOnOneLessonType;
} & Partial<PaymentMethodConfigsDTO>;

const createValidationSchema = (
  selectedMethods: PaymentMethod[],
): yup.AnyObjectSchema => {
  const baseSchema = yup.object({
    productName: yup.string().required("Inserire un nome del prodotto"),
    variantProductName: yup
      .string()
      .required("Inserire un nome del prodotto variante"),
    priceInEuros: yup
      .number()
      .required("Inserire un prezzo valido")
      .typeError("Inserire un prezzo valido"),
    productClass: yup
      .string()
      .oneOf(Object.values(KnownProduct), "Invalid product class")
      .required("Inserire una classe di prodotto valida"),
    purchaseAcTags: yup.array().of(yup.string().required()),
    refundAcTags: yup.array().of(yup.string().required()),
    bundledProductIds: yup.array().of(yup.string().required()),
    peopleCount: yup
      .number()
      .min(1, "peopleCount must be at least 1")
      .default(1)
      .required(),

    fpsLessonsDurationInWeeks: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .when(["productClass", "oneOnOne"], {
        is: (productClass: KnownProduct, oneOnOne: boolean) => {
          return productClass === KnownProduct.FPS && !oneOnOne;
        },
        then: (schema) => schema.required("Durata obbligatoria"),
        otherwise: (schema) => schema.optional(),
      }),

    fpsUnlimitedLessonsPerWeek: yup.boolean(),

    fpsTotalLessons: yup
      .number()
      .when(["productClass", "fpsUnlimitedLessonsPerWeek", "oneOnOne"], {
        is: (
          productClass: KnownProduct,
          unlimited: boolean,
          oneOnOne: boolean,
        ) => productClass === KnownProduct.FPS && unlimited && !oneOnOne,
        then: (schema) =>
          schema.min(1).required("Numero totale lezioni obbligatorio"),
        otherwise: (schema) => schema.optional(),
      }),

    fpsLessonsPerWeek: yup
      .number()
      .when(["productClass", "fpsUnlimitedLessonsPerWeek", "oneOnOne"], {
        is: (
          productClass: KnownProduct,
          unlimited: boolean,
          oneOnOne: boolean,
        ) => productClass === KnownProduct.FPS && !unlimited && !oneOnOne,
        then: (schema) =>
          schema.min(1).required("Numero di lezioni obbligatorio"),
        otherwise: (schema) => schema.optional(),
      }),

    fpsWarrantyEnabled: yup.boolean(),

    fpsWarrantyMinAttendedLessons: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )

      .when(["productClass", "fpsWarrantyEnabled", "oneOnOne"], {
        is: (
          productClass: KnownProduct,
          fpsWarrantyEnabled: boolean,
          oneOnOne: boolean,
        ) =>
          productClass === KnownProduct.FPS && fpsWarrantyEnabled && !oneOnOne,
        then: (schema) =>
          schema
            .min(1)
            .required("Numero minimo lezioni obbligatorio per la garanzia"),
        otherwise: (schema) => schema.optional(),
      }),

    selectedPaymentMethods: yup
      .array()
      .of(yup.string().oneOf(Object.values(PaymentMethod)))
      .min(1, "Seleziona almeno un metodo di pagamento")
      .required("Seleziona almeno un metodo di pagamento"),

    analyticsTag: yup.string().optional(),
    productDescription: yup.string().optional(),

    userGroupSlug: yup.string().optional(),
    doNotEnroll: yup.boolean().default(false),
    doNotMerge: yup.boolean().default(false),

    isCouponEnabled: yup.boolean().default(false),
    oneOnOne: yup.boolean().default(false),
    oneOnOneTotalLessons: yup.number().when(["productClass", "oneOnOne"], {
      is: (productClass: KnownProduct, oneOnOne: boolean) =>
        productClass === KnownProduct.FPS && oneOnOne,
      then: (schema) =>
        schema.required("Numero totale lezioni obbligatorio per One on One"),
      otherwise: (schema) => schema.optional(),
    }),
    oneOnOneDurationInWeeks: yup.number().when(["productClass", "oneOnOne"], {
      is: (productClass: KnownProduct, oneOnOne: boolean) =>
        productClass === KnownProduct.FPS && oneOnOne,
      then: (schema) =>
        schema
          .min(1)
          .required("Durata in settimane obbligatoria per One on One"),
      otherwise: (schema) => schema.optional(),
    }),
    oneOnOneMaxCancellations: yup.number().when(["productClass", "oneOnOne"], {
      is: (productClass: KnownProduct, oneOnOne: boolean) =>
        productClass === KnownProduct.FPS && oneOnOne,
      then: (schema) =>
        schema
          .min(0)
          .required(
            "Numero massimo di cancellazioni obbligatorio per One on One",
          ),
      otherwise: (schema) => schema.optional(),
    }),
    oneOnOneLessonType: yup
      .string()
      .oneOf(Object.values(OneOnOneLessonType))
      .when(["productClass", "oneOnOne"], {
        is: (productClass: KnownProduct, oneOnOne: boolean) =>
          productClass === KnownProduct.FPS && oneOnOne,
        then: (schema) =>
          schema.required("Tipo di lezione obbligatorio per One on One"),
        otherwise: (schema) => schema.optional(),
      }),
  });

  const paymentMethodsSchema =
    buildPaymentMethodsValidationSchema(selectedMethods);

  return baseSchema.concat(
    paymentMethodsSchema as yup.AnyObjectSchema,
  ) as yup.AnyObjectSchema;
};

function InputWithLabel({
  children,
  label,
}: PropsWithChildren<{ label: string }>) {
  return (
    <div className={"flex flex-col gap-1 flex-1"}>
      <label className={"font-medium"}>{label}</label>
      {children}
    </div>
  );
}

function productToFormValues(
  product: NormasTeachingProductDTO,
): Partial<CreateNormasTeachingProductFormValues> {
  const formValues: Partial<CreateNormasTeachingProductFormValues> = {
    productName: product.productName,
    variantProductName: product.variantProductName,
    priceInEuros: product.priceInCents.amount / 100,
    productClass: product.productClass,
    purchaseAcTags: product.purchaseAcTags,
    refundAcTags: product.refundAcTags,
    bundledProductIds: product.bundledProducts?.map((p) => p._id) || [],
    peopleCount: product.peopleCount,
    analyticsTag: product.analyticsTag,
    productDescription: product.productDescription,
    isCouponEnabled: product.isCouponEnabled ?? false,
  };

  // FPS Product Info
  if (product.fpsProductInfo) {
    formValues.fpsLessonsDurationInWeeks =
      product.fpsProductInfo.durationInWeeks;
    formValues.fpsUnlimitedLessonsPerWeek =
      product.fpsProductInfo.unlimitedLessonsPerWeek ?? true;
    formValues.fpsLessonsPerWeek = product.fpsProductInfo.lessonsPerWeek;
    formValues.fpsTotalLessons = product.fpsProductInfo.totalLessons;
    formValues.fpsExtraWeek = product.fpsProductInfo.extraWeek;
    formValues.fpsWarrantyEnabled = product.fpsProductInfo.warrantyEnabled;
    formValues.fpsWarrantyMinAttendedLessons =
      product.fpsProductInfo.warrantyMinAttendedLessons;
    formValues.userGroupSlug = product.fpsProductInfo.userGroupSlug;
    formValues.doNotEnroll = product.fpsProductInfo.doNotEnroll ?? false;
    formValues.doNotMerge = product.fpsProductInfo.doNotMerge ?? false;
    formValues.oneOnOne = product.fpsProductInfo.oneOnOne ?? false;
    formValues.oneOnOneTotalLessons =
      product.fpsProductInfo.oneOnOneTotalLessons ?? 0;
    formValues.oneOnOneDurationInWeeks =
      product.fpsProductInfo.oneOnOneDurationInWeeks;
    formValues.oneOnOneMaxCancellations =
      product.fpsProductInfo.oneOnOneMaxCancellations;
    formValues.oneOnOneLessonType = product.fpsProductInfo.oneOnOneLessonType;
  }

  // Payment Methods
  const selectedMethods: PaymentMethod[] = [];

  if (product.stripeConfig) {
    selectedMethods.push(PaymentMethod.Stripe);
    formValues.stripeConfig = {
      ...product.stripeConfig,
      oneTimeEnabled: product.stripeConfig.oneTimeEnabled ?? false,
    };
  }

  if (product.heylightConfig) {
    selectedMethods.push(PaymentMethod.HeyLight);
    formValues.heylightConfig = product.heylightConfig;
  }

  formValues.selectedPaymentMethods = selectedMethods;

  return formValues;
}

export function CreateNormasTeachingProductForm({
  initialKnownPurchaseAcTags,
  initialKnownRefundAcTags,
  initialKnownAnalyticsTags,
  products,
  creating,
  onSubmit,
  initialProduct,
  isEditMode = false,
}: {
  initialKnownPurchaseAcTags: string[];
  initialKnownRefundAcTags: string[];
  initialKnownAnalyticsTags: string[];
  products: NormasTeachingProductDTO[];
  onSubmit: (data: CreateNormasTeachingProductFormValues) => void;
  creating: boolean;
  initialProduct?: NormasTeachingProductDTO;
  isEditMode?: boolean;
}) {
  function mapStringsToMultipleSelectOptions(
    strings: string[],
  ): MultipleSelectOption<string>[] {
    return strings.map((s) => ({
      id: s,
      label: s,
    }));
  }

  function mapStringsToSelectOptions(
    strings: string[],
  ): SelectOption<string>[] {
    return strings.map((s) => ({
      id: s,
      name: s,
    }));
  }

  const [acPurchaseTagOptions, setAcPurchaseTagOptions] = useState<
    MultipleSelectOption<string>[]
  >(mapStringsToMultipleSelectOptions(initialKnownPurchaseAcTags));
  const [acRefundTagOptions, setAcRefundTagOptions] = useState<
    MultipleSelectOption<string>[]
  >(mapStringsToMultipleSelectOptions(initialKnownRefundAcTags));
  const [analyticsTagOptions, setAnalyticsTagOptions] = useState<
    SelectOption<string>[]
  >(mapStringsToSelectOptions(initialKnownAnalyticsTags));

  const [selectedMethods, setSelectedMethods] = useState<PaymentMethod[]>([]);

  const defaultValues = initialProduct
    ? productToFormValues(initialProduct)
    : {
        fpsUnlimitedLessonsPerWeek: true,
        fpsWarrantyEnabled: true,
        fpsExtraWeek: false,
        selectedPaymentMethods: [],
        peopleCount: 1,
        doNotEnroll: false,
        doNotMerge: false,
      };

  const {
    control,
    register,
    setValue,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<CreateNormasTeachingProductFormValues>({
    resolver: yupResolver(createValidationSchema(selectedMethods)),
    defaultValues: defaultValues,
  });

  const hasUnlimitedLessonsPerWeek = watch("fpsUnlimitedLessonsPerWeek");
  const hasWarrantyEnabled = watch("fpsWarrantyEnabled");
  const selectedClass = watch("productClass");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedPaymentMethods = watch("selectedPaymentMethods") || [];
  useEffect(() => {
    setSelectedMethods(selectedPaymentMethods);
  }, [selectedPaymentMethods]);

  useEffect(() => {
    if (!isEditMode) {
      if (hasUnlimitedLessonsPerWeek) {
        setValue("fpsTotalLessons", 10, { shouldValidate: true });
      } else {
        setValue("fpsLessonsPerWeek", 2, { shouldValidate: true });
      }
    }
  }, [hasUnlimitedLessonsPerWeek, setValue, isEditMode]);

  useEffect(() => {
    if (!isEditMode && !hasWarrantyEnabled) {
      setValue("fpsWarrantyMinAttendedLessons", 1, { shouldValidate: true });
    }
  }, [hasWarrantyEnabled, setValue, isEditMode]);

  const onSubmitInternal = async (
    data: CreateNormasTeachingProductFormValues,
  ) => {
    onSubmit(data);
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <form className={"flex flex-col gap-4"}>
      {hasErrors && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3">
          <p className="text-red-700 font-medium text-sm">
            Ci sono errori nel form, controlla i campi evidenziati:
          </p>
          <ul className="mt-1 list-disc list-inside text-red-600 text-sm">
            {Object.entries(errors).map(([key, err]) => {
              const message = (err as { message?: string })?.message;
              return message ? <li key={key}>{message}</li> : null;
            })}
          </ul>
        </div>
      )}
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"Nome prodotto"}>
          <TextInput
            {...register("productName")}
            errorMessage={errors?.productName?.message}
          />
        </InputWithLabel>
        <InputWithLabel label={"Nome variante Prodotto"}>
          <TextInput
            {...register("variantProductName")}
            errorMessage={errors?.variantProductName?.message}
          />
        </InputWithLabel>
      </div>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <div className="flex flex-1 gap-4">
          <InputWithLabel label={"Prezzo in Euro"}>
            <TextInput
              errorMessage={errors?.priceInEuros?.message}
              onWheel={(e) => {
                const el = e.target as HTMLInputElement;
                el?.blur();
                e.stopPropagation();
                setTimeout(() => {
                  el?.focus();
                }, 0);
              }}
              type={"number"}
              {...register("priceInEuros")}
            />
          </InputWithLabel>
          <InputWithLabel label="Numero di persone">
            <TextInput
              type="number"
              min={1}
              defaultValue={1}
              {...register("peopleCount")}
              errorMessage={errors?.peopleCount?.message}
            />
          </InputWithLabel>
        </div>

        <InputWithLabel label={"Classe prodotto"}>
          <Controller
            render={({ field }) => {
              return (
                <Select
                  errorMessage={errors?.productClass?.message}
                  value={field.value}
                  onChange={field.onChange}
                  options={Object.entries(KnownProduct).map(([key, value]) => {
                    return {
                      name: key,
                      id: value,
                    };
                  })}
                />
              );
            }}
            name={"productClass"}
            control={control}
          />
        </InputWithLabel>
      </div>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"Active Campaign tag post acquisto"}>
          <Controller
            render={({ field }) => {
              return (
                <MultipleSelect
                  errorMessage={errors?.purchaseAcTags?.message}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={acPurchaseTagOptions}
                  allowCreateInfo={{
                    setOptions: setAcPurchaseTagOptions,
                  }}
                />
              );
            }}
            name={"purchaseAcTags"}
            control={control}
          />
        </InputWithLabel>
        <InputWithLabel label={"Active Campaign tag post refund"}>
          <Controller
            render={({ field }) => {
              return (
                <MultipleSelect
                  errorMessage={errors?.refundAcTags?.message}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={acRefundTagOptions}
                  allowCreateInfo={{
                    setOptions: setAcRefundTagOptions,
                  }}
                />
              );
            }}
            name={"refundAcTags"}
            control={control}
          />
        </InputWithLabel>
      </div>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"Prodotti inclusi"}>
          <Controller
            render={({ field }) => {
              return (
                <MultipleSelect
                  errorMessage={errors?.bundledProductIds?.message}
                  searchable
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={products.map((p) => ({
                    id: p._id,
                    label: p.productName,
                  }))}
                />
              );
            }}
            name={"bundledProductIds"}
            control={control}
          />
        </InputWithLabel>
      </div>

      {selectedClass === KnownProduct.FPS && (
        <div className="flex flex-col gap-4">
          <div>
            <InputWithLabel label="One on One">
              <Controller
                name="oneOnOne"
                control={control}
                render={({ field }) => (
                  <Select
                    errorMessage={errors?.oneOnOne?.message}
                    value={field.value ? "true" : "false"}
                    onChange={(val) => {
                      const newVal = val === "true";
                      field.onChange(newVal);
                    }}
                    options={[
                      { id: "true", name: "Sì" },
                      { id: "false", name: "No" },
                    ]}
                  />
                )}
              />
            </InputWithLabel>
          </div>

          {watch("oneOnOne") ? (
            <div className="flex flex-col gap-4">
              <InputWithLabel label="One on One total Lessons">
                <TextInput
                  type="number"
                  {...register("oneOnOneTotalLessons")}
                  errorMessage={errors?.oneOnOneTotalLessons?.message}
                  min={1}
                />
              </InputWithLabel>
              <div className="flex flex-col md:flex-row gap-4">
                <InputWithLabel label="Durata piano (settimane)">
                  <TextInput
                    type="number"
                    {...register("oneOnOneDurationInWeeks")}
                    errorMessage={errors?.oneOnOneDurationInWeeks?.message}
                    min={1}
                  />
                </InputWithLabel>
                <InputWithLabel label="Cancellazioni massime consentite">
                  <TextInput
                    type="number"
                    {...register("oneOnOneMaxCancellations")}
                    errorMessage={errors?.oneOnOneMaxCancellations?.message}
                    min={0}
                  />
                </InputWithLabel>
              </div>
              <InputWithLabel label="Tipo di lezione One on One">
                <Controller
                  name="oneOnOneLessonType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      errorMessage={errors?.oneOnOneLessonType?.message}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      options={[
                        {
                          id: OneOnOneLessonType.GeneralEnglish,
                          name: "General English",
                        },
                        {
                          id: OneOnOneLessonType.BusinessEnglish,
                          name: "Business English",
                        },
                      ]}
                    />
                  )}
                />
              </InputWithLabel>
            </div>
          ) : null}
          {!watch("oneOnOne") ? (
            <div className="flex flex-col md:flex-row gap-4">
              <InputWithLabel label="Durata lezioni (settimane)">
                <TextInput
                  type="number"
                  {...register("fpsLessonsDurationInWeeks")}
                  errorMessage={errors?.fpsLessonsDurationInWeeks?.message}
                  min={1}
                />
              </InputWithLabel>
              <div className="flex flex-1 gap-4">
                <InputWithLabel label="RLC illimitate a settimana">
                  <Controller
                    name="fpsUnlimitedLessonsPerWeek"
                    control={control}
                    render={({ field }) => (
                      <Select
                        errorMessage={
                          errors?.fpsUnlimitedLessonsPerWeek?.message
                        }
                        value={field.value ? "true" : "false"}
                        onChange={(val) => {
                          const newVal = val === "true";
                          field.onChange(newVal);

                          setValue("fpsExtraWeek", !newVal, {
                            shouldValidate: true,
                          });
                        }}
                        options={[
                          { id: "true", name: "Sì" },
                          { id: "false", name: "No" },
                        ]}
                      />
                    )}
                  />
                </InputWithLabel>
                {watch("fpsUnlimitedLessonsPerWeek") ? (
                  <InputWithLabel label="Numero totale lezioni">
                    <TextInput
                      type="number"
                      {...register("fpsTotalLessons")}
                      errorMessage={errors?.fpsTotalLessons?.message}
                      min={1}
                    />
                  </InputWithLabel>
                ) : (
                  <InputWithLabel label="Lezioni a settimana">
                    <TextInput
                      type="number"
                      {...register("fpsLessonsPerWeek")}
                      errorMessage={errors?.fpsLessonsPerWeek?.message}
                      min={1}
                    />
                  </InputWithLabel>
                )}
              </div>
            </div>
          ) : null}

          {!watch("oneOnOne") ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-1 gap-4">
                <InputWithLabel label="Garanzia abilitata">
                  <Controller
                    name="fpsWarrantyEnabled"
                    control={control}
                    render={({ field }) => (
                      <Select
                        errorMessage={errors?.fpsWarrantyEnabled?.message}
                        value={field.value ? "true" : "false"}
                        onChange={(val) => field.onChange(val === "true")}
                        options={[
                          { id: "true", name: "Sì" },
                          { id: "false", name: "No" },
                        ]}
                      />
                    )}
                  />
                </InputWithLabel>

                {watch("fpsWarrantyEnabled") && (
                  <InputWithLabel label="Minimo lezioni frequentate per garanzia">
                    <TextInput
                      type="number"
                      {...register("fpsWarrantyMinAttendedLessons")}
                      errorMessage={
                        errors?.fpsWarrantyMinAttendedLessons?.message
                      }
                      min={1}
                    />
                  </InputWithLabel>
                )}
              </div>
            </div>
          ) : null}
          <div className={"flex flex-col md:flex-row gap-4"}>
            <InputWithLabel label={"User group slug (opzionale)"}>
              <TextInput
                {...register("userGroupSlug")}
                errorMessage={errors?.userGroupSlug?.message}
              />
            </InputWithLabel>
            <InputWithLabel
              label={"Non iscrivere automaticamente (doNotEnroll)"}
            >
              <Controller
                name="doNotEnroll"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ? "true" : "false"}
                    onChange={(val) => field.onChange(val === "true")}
                    options={[
                      { id: "false", name: "No" },
                      { id: "true", name: "Yes" },
                    ]}
                  />
                )}
              />
            </InputWithLabel>
          </div>
          {!watch("oneOnOne") && (
            <Controller
              name="doNotMerge"
              control={control}
              render={({ field }) => (
                <CheckBox
                  label={<span>Non fare il merge (doNotMerge)</span>}
                  value={field.value ?? false}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </div>
      )}

      <div>
        <InputWithLabel label={"Analytics tag"}>
          <Controller
            render={({ field }) => {
              return (
                <Select
                  errorMessage={errors?.analyticsTag?.message}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  options={analyticsTagOptions}
                  allowCreateInfo={{
                    setOptions: setAnalyticsTagOptions,
                  }}
                />
              );
            }}
            name={"analyticsTag"}
            control={control}
          />
        </InputWithLabel>
      </div>

      <div>
        <InputWithLabel label={"Descrizione Prodotto"}>
          <textarea
            {...register("productDescription")}
            className="w-full min-h-[150px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Inserisci la descrizione del prodotto, visualizzata all'utente in fase di checkout. Supporta Markdown per formattazione e immagini."
          />
          {errors?.productDescription?.message && (
            <p className="text-red-600 text-sm mt-1">
              {errors.productDescription.message}
            </p>
          )}
        </InputWithLabel>
        <p className="text-sm text-gray-500 mt-1">
          Puoi usare Markdown: **grassetto**, *corsivo*, liste, link e immagini
          ![alt](url) oppure{" "}
          {`<img src="url" alt="alt" width="400" style="display: block; margin: 0 auto;" />`}
        </p>
      </div>

      <Controller
        render={({ field }) => {
          return (
            <CheckBox
              label={<span>Abilita prodotto ai coupon</span>}
              value={field.value ?? false}
              onChange={field.onChange}
            />
          );
        }}
        name={"isCouponEnabled"}
        control={control}
      />

      <div className="border-t pt-6 mt-6">
        <h3 className="text-lg font-bold mb-4">
          Metodi di pagamento abilitati
        </h3>

        <div className="space-y-3 mb-4">
          {getAvailablePaymentMethods().map((method) => (
            <Controller
              key={method}
              name="selectedPaymentMethods"
              control={control}
              render={({ field }) => {
                const isChecked = field.value?.includes(method) || false;
                const config = getPaymentMethodConfig(method);

                return (
                  <CheckBox
                    label={
                      <div>
                        <span className="font-medium">
                          {getPaymentMethodName(method)}
                        </span>
                      </div>
                    }
                    value={isChecked}
                    onChange={(checked) => {
                      const currentValues = field.value || [];
                      const newMethods = checked
                        ? [...currentValues, method]
                        : currentValues.filter((m) => m !== method);

                      field.onChange(newMethods);

                      if (checked && config) {
                        setValue(
                          config.configFieldName,
                          config.defaultValues || undefined,
                        );
                      } else if (!checked && config) {
                        setValue(config.configFieldName, undefined);
                      }
                    }}
                  />
                );
              }}
            />
          ))}
        </div>

        {errors.selectedPaymentMethods && (
          <p className="text-red-600 text-sm mb-4">
            {errors.selectedPaymentMethods.message}
          </p>
        )}

        {/* Renderizza i campi di configurazione per ogni metodo selezionato */}
        {selectedPaymentMethods.length > 0 && (
          <div className="space-y-6 mt-6">
            {selectedPaymentMethods.map((method) => {
              const config = getPaymentMethodConfig(method);
              if (!config) return null;

              const ConfigComponent = config.ConfigComponent;

              return (
                <div key={method} className="border-t pt-4">
                  <h4 className="font-semibold mb-3">
                    Configurazione {getPaymentMethodName(method)}
                  </h4>
                  <ConfigComponent
                    fieldName={config.configFieldName}
                    control={control as unknown as Control<FieldValues>}
                    errors={errors}
                    register={
                      register as unknown as UseFormRegister<FieldValues>
                    }
                    watch={watch as unknown as UseFormWatch<FieldValues>}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PrimaryButton
        loading={creating}
        label={isEditMode ? "Aggiorna" : "Crea"}
        onClick={handleSubmit(onSubmitInternal, (validationErrors) => {
          const summary = Object.fromEntries(
            Object.entries(validationErrors).map(([key, err]) => [
              key,
              {
                message: (err as { message?: string })?.message,
                type: (err as { type?: string })?.type,
              },
            ]),
          );
          console.error("Errori di validazione:", summary);
          window.scrollTo({ top: 0, behavior: "smooth" });
        })}
      />
    </form>
  );
}
